// Assessment utility functions for CRUD, versioning, and scoring

export const createAssessment = (name = "New Assessment") => ({
  id: "ca_" + Date.now(),
  name,
  description: "",
  questions: [],
  live: false,
  allowRetake: true,
  showResults: "both",
  addedAt: new Date().toISOString(),
  version: 1,
  parentId: null,
  isLocked: false,
  lockedAt: null,
  scoreBands: []
});

export const createQuestion = (type = "scored") => ({
  id: "caq_" + Date.now(),
  q: "",
  type,
  opts: type === "scored"
    ? [
        { label: "Strongly Disagree", score: 1 },
        { label: "Disagree", score: 2 },
        { label: "Neutral", score: 3 },
        { label: "Agree", score: 4 },
        { label: "Strongly Agree", score: 5 }
      ]
    : [{ label: "" }]
});

export const publishAssessment = (assessments, assessmentId) => {
  const assessment = assessments.find(a => a.id === assessmentId);
  if (!assessment) return assessments;

  // If this is a draft with parentId, demote old parent
  if (assessment.parentId && !assessment.live) {
    return assessments.map(a => {
      if (a.id === assessment.parentId && a.live) {
        return { ...a, live: false };
      }
      if (a.id === assessmentId) {
        return { ...a, live: true, addedAt: new Date().toISOString() };
      }
      return a;
    });
  }

  // Simple case: toggle current assessment live status
  return assessments.map(a =>
    a.id === assessmentId ? { ...a, live: !a.live } : a
  );
};

export const createNewVersion = (assessments, assessmentId) => {
  const original = assessments.find(a => a.id === assessmentId);
  if (!original) return assessments;

  const relatedVersions = assessments.filter(
    a => a.parentId === assessmentId || a.id === assessmentId
  );
  const maxVersion = Math.max(
    ...relatedVersions.map(a => a.version || 1),
    original.version || 1
  );

  const newVersion = {
    id: "ca_" + Date.now(),
    ...original,
    live: false,
    version: maxVersion + 1,
    parentId: assessmentId,
    addedAt: new Date().toISOString(),
    questions: JSON.parse(JSON.stringify(original.questions))
  };

  return [...assessments, newVersion];
};

export const getVersionHistory = (assessments, assessmentId) => {
  const versions = assessments.filter(
    a => a.id === assessmentId || a.parentId === assessmentId
  );
  return versions.sort((a, b) => (b.version || 1) - (a.version || 1));
};

export const restoreVersion = (assessments, versionId) => {
  const version = assessments.find(a => a.id === versionId);
  if (!version) return assessments;

  const nextVersion = Math.max(...assessments.map(a => a.version || 1)) + 1;

  return assessments.map(a => {
    // Demote old live versions
    if (a.parentId === version.parentId && a.live && a.id !== versionId) {
      return { ...a, live: false };
    }
    // Update parent with restored questions
    if (a.id === version.parentId) {
      return {
        ...a,
        questions: JSON.parse(JSON.stringify(version.questions)),
        description: version.description,
        allowRetake: version.allowRetake,
        showResults: version.showResults,
        scoreBands: version.scoreBands,
        version: nextVersion,
        live: true
      };
    }
    return a;
  });
};

export const calculateScore = (responses, questions) => {
  if (!Array.isArray(responses) || responses.length === 0) return 0;
  const scores = responses
    .map(r => parseInt(r.answer) || 0)
    .filter(s => s > 0);
  return scores.length > 0
    ? (scores.reduce((a, b) => a + b) / scores.length).toFixed(2)
    : 0;
};

export const getScoreBand = (bands, score) => {
  if (!Array.isArray(bands) || bands.length === 0) return null;
  return bands.find(b => score >= b.min && score <= b.max) || null;
};

export const loadAssessments = async (sbGetSetting) => {
  try {
    return await sbGetSetting("origamic-custom-assessments") || [];
  } catch (err) {
    console.error("Error loading assessments:", err);
    return [];
  }
};

export const saveAssessments = async (assessments, sbSetSetting) => {
  try {
    await sbSetSetting("origamic-custom-assessments", assessments);
    return true;
  } catch (err) {
    console.error("Error saving assessments:", err);
    throw err;
  }
};
